import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Circle, Sparkles, ChevronRight, MapPin, Briefcase, Star } from "lucide-react";

/** Profile completeness widget — pulls /api/seeker/profile-completeness and
 * renders a percentage donut + missing fields checklist. The Edit link
 * deep-links to /seeker/profile so seekers can finish the profile.
 */
export function ProfileCompletenessWidget() {
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancel = false;
        api.get("/seeker/profile-completeness")
            .then(({ data }) => { if (!cancel) setData(data); })
            .catch(() => { /* silent */ });
        return () => { cancel = true; };
    }, []);
    if (!data) return null;

    const pct = data.pct || 0;
    const ring = `conic-gradient(#002FA7 ${pct * 3.6}deg, #e2e8f0 0deg)`;
    const isDone = pct >= 100;

    return (
        <div className="border border-border bg-white p-4" data-testid="profile-completeness-widget">
            <div className="flex items-center gap-4">
                <div
                    className="relative w-20 h-20 rounded-full grid place-items-center shrink-0"
                    style={{ background: ring }}
                    data-testid="profile-completeness-ring"
                >
                    <div className="w-16 h-16 bg-white rounded-full grid place-items-center">
                        <div className="text-center">
                            <div className="text-xl font-display font-bold text-ikb leading-none">{pct}%</div>
                            <div className="text-[8px] font-mono uppercase tracking-widest text-foreground/50">complete</div>
                        </div>
                    </div>
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-mono uppercase tracking-widest text-foreground/60">Your profile</div>
                    {isDone ? (
                        <div className="text-sm font-medium text-emerald-700 flex items-center gap-1.5 mt-1">
                            <CheckCircle2 className="w-4 h-4" /> Profile complete — you're getting top recommendations
                        </div>
                    ) : (
                        <>
                            <div className="text-sm font-medium mt-1">Boost your visibility — complete {data.missing.length} more field{data.missing.length === 1 ? "" : "s"}</div>
                            <Link
                                to="/seeker/profile"
                                className="inline-flex items-center gap-1 text-[12px] font-mono uppercase tracking-widest text-ikb hover:underline mt-1"
                                data-testid="profile-completeness-edit"
                            >
                                Complete profile <ChevronRight className="w-3 h-3" />
                            </Link>
                        </>
                    )}
                </div>
            </div>
            {data.missing.length > 0 ? (
                <ul className="mt-3 grid sm:grid-cols-2 gap-y-1 gap-x-3 text-[12px]" data-testid="profile-completeness-missing">
                    {data.missing.slice(0, 6).map((m) => (
                        <li key={m.field} className="flex items-center gap-1.5 text-foreground/70">
                            <Circle className="w-3 h-3 text-foreground/40" />
                            <span>{m.label}</span>
                        </li>
                    ))}
                    {data.filled.slice(0, Math.max(0, 6 - data.missing.length)).map((f) => (
                        <li key={f.field} className="flex items-center gap-1.5 text-emerald-700">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>{f.label}</span>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

/** Recommended jobs section — pulls /api/seeker/recommended-jobs and renders
 * up to 6 ranked cards. If the seeker hasn't added skills/location yet, shows
 * a CTA to fill the profile first.
 */
export function RecommendedJobsSection({ onSaveToggle, savedIds = new Set() }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancel = false;
        api.get("/seeker/recommended-jobs", { params: { limit: 6 } })
            .then(({ data }) => { if (!cancel) setData(data); })
            .catch(() => { /* silent */ });
        return () => { cancel = true; };
    }, []);
    if (!data) return null;

    if (!data.has_skills) {
        return (
            <div className="border border-dashed border-border bg-secondary/30 p-4" data-testid="recommended-jobs-skill-cta">
                <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-ikb mt-0.5" />
                    <div>
                        <div className="text-sm font-medium">Add your skills to unlock personalised recommendations</div>
                        <div className="text-[12px] text-foreground/60 mt-1">
                            We use your skills + location to rank fresh openings. Takes about 60 seconds.
                        </div>
                        <Link to="/seeker/profile" className="inline-flex items-center gap-1 mt-2 text-[12px] font-mono uppercase tracking-widest text-ikb hover:underline">
                            Add skills <ChevronRight className="w-3 h-3" />
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (!data.items || data.items.length === 0) {
        return (
            <div className="text-[12px] text-foreground/50 italic" data-testid="recommended-jobs-empty">
                No matches right now — we'll keep looking.
            </div>
        );
    }

    return (
        <div data-testid="recommended-jobs-section">
            <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-ikb" />
                <h2 className="text-sm font-display font-bold uppercase tracking-widest">Recommended for you</h2>
                <span className="text-[10px] font-mono text-foreground/50 ml-auto">based on your skills</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {data.items.map((j) => {
                    const pct = Math.round((j.match_score || 0) * 100);
                    return (
                        <Link
                            key={j.id}
                            to={`/apply/${j.id}`}
                            className="min-w-0 border border-border bg-white p-3 hover:border-ikb hover:shadow-sm transition relative group"
                            data-testid={`recommended-job-${j.id}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="font-medium text-sm truncate group-hover:text-ikb">{j.title}</div>
                                    <div className="text-[11px] text-foreground/60 truncate flex items-center gap-1 mt-0.5">
                                        <MapPin className="w-3 h-3" /> {j.location || "—"}
                                        {j.work_mode ? <span className="ml-1">· {j.work_mode}</span> : null}
                                    </div>
                                </div>
                                <span
                                    className="inline-flex items-center gap-0.5 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-800 shrink-0"
                                    title={`Match score ${pct}%`}
                                >
                                    <Star className="w-3 h-3" /> {pct}%
                                </span>
                            </div>
                            {j.compensation_label ? (
                                <div className="text-[11px] text-foreground/70 mt-2 flex items-center gap-1">
                                    <Briefcase className="w-3 h-3" /> {j.compensation_label}
                                </div>
                            ) : null}
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
